import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import PmAnalyticsFilterBar from '../../components/performance/PmAnalyticsFilterBar';
import usePmAnalyticsFilters, { latestPeriod } from '../../components/performance/pmAnalyticsFilters';
import { Grid, PageEmpty, PageWrap } from '../performance-operations/styles';
import GoalFilterStrip from './components/GoalFilterStrip';
import GoalKpiCards from './components/GoalKpiCards';
import BandsPanel from './components/BandsPanel';
import GoalSeriesPanel from './components/GoalSeriesPanel';
import GoalInsightsPanel from './components/GoalInsightsPanel';
import GoalListPanel from './components/GoalListPanel';
import GoalTreePanel from './components/GoalTreePanel';
import GoalDetailDrawer from './components/GoalDetailDrawer';
import { fetchPeriods, useGoalFilters, useTabParam } from './config';

// P3 (PM analitik tamamlama, 2026-10-08) — E04 Hedef ve KPI Analitiği (/performance/goal-analytics).
// İK (kiracı) ve yönetici (alt ağaç) görür; kapsam backend'de (PmAnalyticsDataAccess.ScopedParticipantsAsync —
// diğerleri 403 performance.analytics.forbidden). Desen İK Operasyon Panosu: ortak süzgeç şeridi (F0, URL) + E04 hedef
// süzgeçleri (URL), panel başına bağımsız yükleme/hata/Tekrar Dene, grafiklerde "Tablo Olarak Göster". Akış:
// bant → hedef listesi → hedef detayı → güncelleme geçmişi; "Hedef Bağlantıları" sekmesinde şirket → birim → bireysel ağaç.
// Sayfa gövdesi TEK kaydırmadır (liste DataTable'ı kendi içinde kaydırmaz — tableBodyMaxHeight="none").
export default function PerformanceGoalAnalyticsPage() {
  const { t } = useTranslation();
  const { filters, setFilters, reset } = usePmAnalyticsFilters();
  const { goalFilters, setGoalFilters, resetGoalFilters } = useGoalFilters();
  const { tab, setTab } = useTabParam();
  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [metas, setMetas] = useState({});
  const [selectedGoal, setSelectedGoal] = useState(null);   // zaman serisi paneli
  const [detailGoalId, setDetailGoalId] = useState(null);   // detay çekmecesi
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    return () => { alive = false; };
  }, []);

  // Dönem zorunlu ve temizlenemez: URL'de yoksa (ya da artık yoksa) en yeni dönem seçilir.
  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!filters.periodId || !periods.some((p) => p.id === filters.periodId)) {
      setFilters((f) => ({ ...f, periodId: latestPeriod(periods).id }));
    }
  }, [periodsLoaded, periods, filters.periodId, setFilters]);

  // Süzgeç/dönem değişince seçili hedef ve güncellik bilgisi düşer.
  const filterKey = JSON.stringify(filters);
  useEffect(() => { setMetas({}); setSelectedGoal(null); }, [filterKey]);
  const onMeta = useCallback((key, meta) => setMetas((m) => ({ ...m, [key]: meta })), []);
  const lastUpdated = useMemo(() => Object.values(metas).map((m) => m?.calculatedAt).filter(Boolean).sort()[0] || null, [metas]);
  const stale = useMemo(() => Object.values(metas).some((m) => m?.stale), [metas]);

  // URL'deki dönem listede yoksa (bayat yer imi) paneller o dönemle istek atmaz; dönem etkisi en yeni döneme geçirir.
  const ready = periodsLoaded && periods.some((p) => p.id === filters.periodId);
  const openGoal = useCallback((row) => {
    setSelectedGoal({ goalId: row.goalId, title: row.title || row.goalTitle });
    setDetailGoalId(row.goalId);
  }, []);
  const onChanged = useCallback(() => setRefreshKey((k) => k + 1), []);

  const tabs = useMemo(() => [
    { key: 'overview', label: t('performance.goalAnalytics.tabs.overview') },
    { key: 'links', label: t('performance.goalAnalytics.tabs.links') },
  ], [t]);

  return (
    <PageWrap>
      <Breadcrumbs items={[{ label: t('performance.goalAnalytics.title') }]} />
      {periodsLoaded && periods.length === 0 ? <PageEmpty data-state="no-periods">{t('performance.goalAnalytics.noPeriods')}</PageEmpty> : null}
      {periods.length > 0 ? (
        <PmAnalyticsFilterBar
          periods={periods}
          filters={filters}
          onChange={setFilters}
          onReset={reset}
          lastUpdated={lastUpdated}
          stale={stale}
          groupMembership={filters.groupIds.length ? 'current' : null}
        />
      ) : null}
      {ready ? (
        <>
          <GoalFilterStrip goalFilters={goalFilters} onChange={setGoalFilters} onReset={resetGoalFilters} />
          <Tabs tabs={tabs} activeTab={tab} onChange={setTab} />
          {tab === 'overview' ? (
            <Grid key={`overview-${filters.periodId}`} data-tab="overview">
              <GoalKpiCards
                filters={filters}
                goalFilters={goalFilters}
                onMeta={onMeta}
                onDrill={setGoalFilters}
                onOpenLinks={() => setTab('links')}
                refreshKey={refreshKey}
              />
              <BandsPanel filters={filters} goalFilters={goalFilters} onMeta={onMeta} onDrill={setGoalFilters} refreshKey={refreshKey} />
              <GoalSeriesPanel filters={filters} selected={selectedGoal} />
              <GoalInsightsPanel filters={filters} goalFilters={goalFilters} onOpenGoal={openGoal} refreshKey={refreshKey} />
              <GoalListPanel
                filters={filters}
                goalFilters={goalFilters}
                onOpenGoal={openGoal}
                refreshKey={refreshKey}
                personScope={metas.goalSummary?.personScope}
              />
            </Grid>
          ) : (
            <Grid key={`links-${filters.periodId}`} data-tab="links">
              <GoalKpiCards filters={filters} goalFilters={goalFilters} onMeta={onMeta} onDrill={setGoalFilters} codes={['M61', 'M62']} refreshKey={refreshKey} />
              <GoalTreePanel
                filters={filters}
                onShowGoals={(patch) => { setGoalFilters((f) => ({ ...f, ...patch })); setTab('overview'); }}
                refreshKey={refreshKey}
              />
            </Grid>
          )}
          <GoalDetailDrawer
            filters={filters}
            goalId={detailGoalId}
            open={!!detailGoalId}
            onClose={() => setDetailGoalId(null)}
            onChanged={onChanged}
          />
        </>
      ) : null}
    </PageWrap>
  );
}
